// @flow
import SignProcessConfig from './SignProcessConfig';
import { doGet, doPost, doPostMultipart } from '../util/api';
import ApiResponse from './ApiResponse';
import { PaymentTypes, type AgreementProps } from './props';
import TransferData from './TransferData';
import PaymentData from './PaymentData';
import { durationLabel, durationValue } from '../util/common';

class RenewalSignProcess {
  code: string;
  processType: string;
  state: string;
  price: number;
  valueDuration: string;
  daysDuration: number;
  priceCode: string;
  paymentType: ?string;
  paymentTypes: string[];
  billToFinalCustomer: boolean;
  billingName: string;
  billingEmail: string;
  billingLegalIDType: string;
  billingLegalID: string;
  billingAddress: string;
  transferData: ?TransferData;
  nextProcessCode: ?string;
  hasPaymentProcessed: boolean;

  static parseList(raw?: any): RenewalSignProcess[] {
    let list: RenewalSignProcess[] = [];
    if (raw && Array.isArray(raw)) {
      for (let item of raw) {
        const process: ?RenewalSignProcess = RenewalSignProcess.parse(item);
        if (process) {
          list.push(process);
        }
      }
    }
    return list;
  }

  static parse(raw: any, config?: SignProcessConfig) {
    if (raw) {
      const process = new RenewalSignProcess();
      process.copyFrom(raw, config);
      return process;
    }
  }

  copyFrom(raw: any, config?: SignProcessConfig) {
    this.code = raw.code;
    this.processType = raw.processType;
    this.state = raw.state || 'DATA_LOADED'; // Use actual state from server
    this.price = raw.price;
    this.priceCode = raw.priceCode;
    this.paymentType = raw.paymentType;
    this.nextProcessCode = raw.nextProcessCode;
    this.billToFinalCustomer = !!raw.billToFinalCustomer;
    this.billingName = raw.billingName;
    this.billingEmail = raw.billingEmail;
    this.billingLegalIDType = raw.billingLegalIDType;
    this.billingLegalID = raw.billingLegalID;
    this.billingAddress = raw.billingAddress;
    this.transferData = TransferData.parse(raw.transferData);
    this.paymentTypes = [PaymentTypes.CREDIT_CARD, PaymentTypes.TRANSFER];
    this.hasPaymentProcessed = !!raw.hasPaymentProcessed;

    // Handle duration calculation with config
    if (config && config.processTypes && config.processTypes[this.processType]) {
      const priceItem = config.processTypes[this.processType].find((item) => item.code === this.priceCode);
      if (priceItem) {
        this.daysDuration = durationValue(priceItem.daysDuration);
        this.valueDuration = durationLabel(priceItem.daysDuration);
      } else {
        // Fallback values
        this.daysDuration = raw.daysDuration || 365;
        this.valueDuration = raw.valueDuration || 'YEAR';
      }
    } else {
      // Use raw values as fallback
      this.daysDuration = raw.daysDuration || 365;
      this.valueDuration = raw.valueDuration || 'YEAR';
    }
  }

  isDraft(): boolean {
    return this.state === 'DRAFT';
  }

  isDataLoaded(): boolean {
    return this.state === 'DATA_LOADED';
  }

  hasDataLoaded(): boolean {
    return !this.isDraft();
  }

  isDocumentsLoaded(): boolean {
    return this.state === 'DOCUMENTS_LOADED';
  }

  hasBilling(): boolean {
    return this.billToFinalCustomer || !!this.billingLegalIDType;
  }

  hasDocumentsLoaded(): boolean {
    return this.hasDataLoaded() && !this.isDataLoaded();
  }

  isBillingLoaded(): boolean {
    return this.state === 'BILLING_LOADED';
  }

  hasBillingLoaded(): boolean {
    return this.hasDocumentsLoaded() && !this.isDocumentsLoaded();
  }

  isPaymentLoaded(): boolean {
    return this.state === 'PAYMENT_LOADED';
  }

  hasPaymentLoaded(): boolean {
    return this.hasBillingLoaded() && !this.isBillingLoaded();
  }

  isAgreementLoaded(): boolean {
    return this.state === 'AGREEMENT_LOADED';
  }

  hasAgreementLoaded(): boolean {
    return this.hasPaymentLoaded() && !this.isPaymentLoaded();
  }

  isVideoLoaded(): boolean {
    return this.state === 'VIDEO_LOADED';
  }

  hasVideoLoaded(): boolean {
    return this.hasAgreementLoaded() && !this.isAgreementLoaded();
  }

  isDocumentsValidated(): boolean {
    return this.state === 'DOCUMENTS_VALIDATED';
  }

  hasDocumentsValidated(): boolean {
    return this.hasVideoLoaded() && !this.isVideoLoaded();
  }

  isBillingValidated(): boolean {
    return this.state === 'BILLING_VALIDATED';
  }

  hasBillingValidated(): boolean {
    return this.hasDocumentsValidated() && !this.isDocumentsValidated();
  }

  isPaymentValidated(): boolean {
    return this.state === 'PAYMENT_VALIDATED';
  }

  hasPaymentValidated(): boolean {
    return this.hasBillingValidated() && !this.isBillingValidated();
  }

  isDataValidated(): boolean {
    return this.state === 'DATA_VALIDATED';
  }

  hasDataValidated(): boolean {
    return this.hasPaymentValidated() && !this.isPaymentValidated();
  }

  isVendorReady(): boolean {
    return this.state === 'VENDOR_READY';
  }

  isVendorProcessing(): boolean {
    return this.state === 'VENDOR_PROCESSING';
  }

  isCustomerActivated(): boolean {
    return this.state === 'CUSTOMER_ACTIVATED';
  }

  isProcessing(): boolean {
    return this.isVendorReady() || this.isVendorProcessing() || this.isCustomerActivated();
  }

  isVendorCompleted(): boolean {
    return this.state === 'VENDOR_COMPLETED';
  }

  isVendorRejected(): boolean {
    return this.state === 'VENDOR_REJECTED';
  }

  isRevoked(): boolean {
    return this.state === 'REVOKED';
  }

  isCancelled(): boolean {
    return this.state === 'CANCELLED';
  }

  isCompleted(): boolean {
    return this.isVendorCompleted() || this.isVendorRejected() || this.isRevoked() || this.isCancelled();
  }

  isNaturalProcessType(): boolean {
    return (
      this.processType === SignProcessConfig.signProcessTypes.NATURAL_PERSON ||
      SignProcessConfig.signProcessTypes.NATURAL_PERSON_RUC
    );
  }

  canCreateInvoice(): boolean {
    return this.daysDuration >= 365 && this.isNaturalProcessType();
  }

  isLegalProcessType(): boolean {
    return this.processType === SignProcessConfig.signProcessTypes.LEGAL_PERSON;
  }

  needTributaryId(): boolean {
    return this.forInvoicing && this.isNaturalProcessType();
  }

  fullName(): string {
    let value = this.name;
    if (!!this.lastname) {
      value += ` ${this.lastname}`;
    }
    return value;
  }

  hasSameBillingAndData(): boolean {
    if (this.billToFinalCustomer) {
      return false;
    }
    if (
      this.billingName === undefined &&
      this.billingEmail === undefined &&
      this.billingLegalIDType === undefined &&
      this.billingLegalID === undefined &&
      this.billingAddress === undefined
    ) {
      return true;
    }
    return (
      this.fullName() === this.billingName &&
      this.email === this.billingEmail &&
      this.legalIDType === this.billingLegalIDType &&
      this.legalID === this.billingLegalID &&
      this.addressOfResidence === this.billingAddress
    );
  }

  toDataMap(): any {
    return {
      name: this.name,
      lastname: this.lastname,
      email: this.email,
      phone: this.phone,
      legalIDType: this.legalIDType,
      legalID: this.legalID,
      legalIDExpiresAt: this.legalIDExpiresAt,
      idDocumentCode: this.idDocumentCode,
      tributaryName: this.tributaryName,
      tributaryLegalID: this.tributaryLegalID,
      countryOfResidence: this.countryOfResidence,
      provinceOfResidence: this.provinceOfResidence,
      cityOfResidence: this.cityOfResidence,
      addressOfResidence: this.addressOfResidence,
      zipCode: this.zipCode,
      forInvoicing: this.forInvoicing,
      paymentType: this.paymentType,
      gender: this.gender,
      birthday: this.birthday,
      countryOfBirth: this.countryOfBirth,
      targets: this.targets,
      hasPaymentProcessed: this.hasPaymentProcessed,
    };
  }

  getContractAgreementUrl(token: ?string): string {
    if (!!token && token.length < 15) {
      return `/customer/retail-sign-process/${this.code}/${token}/contract-agreement`;
    }
    return this.contractAgreementUrl;
  }

  getTemplateAgreementUrl(token: ?string): string {
    if (!!token && token.length < 15) {
      return `/customer/retail-sign-process/${this.code}/${token}/template-agreement`;
    }
    return this.templateAgreementUrl;
  }

  getPaymentData(paymentType: ?string): Promise<PaymentData> {
    let type = 'stripe';
    if (paymentType === PaymentTypes.PAYPHONE.toLowerCase()) {
      type = PaymentTypes.PAYPHONE.toLowerCase();
    }
    if (paymentType === PaymentTypes.DEUNA.toLowerCase()) {
      type = PaymentTypes.DEUNA.toLowerCase();
    }
    const url = `/customer/payment/renewal-sign-process/${this.code}/data/${type}`;
    return new Promise((resolve) => {
      doGet(url).then((response: ApiResponse) => {
        const data = new PaymentData();
        if (response.ok) {
          data.copyFrom(response.data);
        } else if (!response.is401Error()) {
          data.error = String(response.message);
        }
        resolve(data);
      });
    });
  }

  saveVideo(file: File, notAttached?: Boolean): Promise<ApiResponse> {
    const data = {
      name: file.name,
      notAttached: !!notAttached,
    };
    const files = {
      file: file,
    };
    const url = `/customer/sign-process/${this.code}/verification-video`;
    return new Promise((resolve) => {
      doPostMultipart(url, data, files).then((response: ApiResponse) => {
        resolve(response);
      });
    });
  }

  applyCoupon(coupon?: string): Promise<ApiResponse> {
    const data = {
      coupon,
    };
    const url = `/customer/sign-process/${this.code}/apply-coupon`;
    return new Promise((resolve) => {
      doPost(url, data).then((response: ApiResponse) => {
        if (response.ok) {
          this.price = response.data.price;
        }
        resolve(response);
      });
    });
  }

  saveAgreement: (AgreementProps) => Promise<any>;
}

export class ProcessResponse {
  process: SignProcess;
  ok: boolean;
  message: string;
}

export default RenewalSignProcess;
